function findPortalShadowRoot() {
    const candidates = [
        '#ghost-portal-root',
        '#portal-root',
        '[id^="ghost-portal"]',
        '[id^="gh-portal"]'
    ];
    for (const sel of candidates) {
        const el = document.querySelector(sel);
        if (el && el.shadowRoot) return el.shadowRoot;
    }
    // Fallback: scan a few top-level divs for open shadow roots
    const all = document.querySelectorAll('div');
    for (const el of all) {
        if (el.shadowRoot && el.shadowRoot.querySelector('.gh-portal-triggerbtn')) {
            return el.shadowRoot;
        }
    }
    return null;
}

function injectStylesIntoPortal() {
    const shadow = findPortalShadowRoot();
    if (!shadow) return false;
    // Proceed even if trigger isn't present; we may want to style the opened modal

    const docStyles = getComputedStyle(document.documentElement);
    const colorText = docStyles.getPropertyValue('--color-text')?.trim() || '#f5f5f5';
    const colorBorder = docStyles.getPropertyValue('--color-border')?.trim() || '#2a2a2a';
    const colorPrimary = docStyles.getPropertyValue('--color-primary')?.trim() || '#1e40af';
    const colorBg = docStyles.getPropertyValue('--color-bg')?.trim() || '#0a0a0a';
    const colorBgSecondary = docStyles.getPropertyValue('--color-bg-secondary')?.trim() || '#1a1a1a';
    const colorTextMuted = docStyles.getPropertyValue('--color-text-muted')?.trim() || '#9ca3af';

    if (shadow.getElementById('theme-portal-button-style')) return true;

    const style = document.createElement('style');
    style.id = 'theme-portal-button-style';
    style.textContent = `
      /* Force-hide portal trigger button in shadow DOM */
      .gh-portal-triggerbtn-wrap,
      .gh-portal-triggerbtn-wrapper,
      .gh-portal-triggerbtn-container,
      .gh-portal-triggerbtn,
      [data-testid="portal-trigger-button"] {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }
      :host { --brandcolor: ${colorPrimary}; font-family: var(--font-sans-serif) !important; }
      .gh-portal-triggerbtn-wrap, .gh-portal-triggerbtn-wrap *,
      .gh-portal-triggerbtn-wrapper, .gh-portal-triggerbtn-wrapper *,
      .gh-portal-triggerbtn-container, .gh-portal-triggerbtn-container * {
        font-family: var(--font-sans-serif) !important;
      }
      .gh-portal-triggerbtn-wrap {
        right: 24px !important;
        bottom: 24px !important;
      }
      .gh-portal-triggerbtn,
      button.gh-portal-triggerbtn,
      a.gh-portal-triggerbtn {
        background: rgba(0,0,0,0.6) !important;
        color: ${colorText} !important;
        border: 1px solid ${colorBorder} !important;
        border-radius: 0 !important;
        box-shadow: none !important;
        text-transform: uppercase !important;
        letter-spacing: 0.06em !important;
        height: 40px !important;
        line-height: 40px !important;
        padding: 0 16px !important;
        background-image: none !important;
      }
      /* Some portals render the trigger as a generic ghost button */
      a.gh-button.gh-portal-close {
        background: rgba(0,0,0,0.6) !important;
        color: ${colorText} !important;
        border: 1px solid ${colorBorder} !important;
        border-radius: 0 !important;
        box-shadow: none !important;
        text-transform: uppercase !important;
        letter-spacing: 0.06em !important;
        height: 40px !important;
        line-height: 40px !important;
        padding: 0 16px !important;
      }
      a.gh-button.gh-portal-close:hover {
        color: ${colorPrimary} !important;
        border-color: ${colorPrimary} !important;
        background: rgba(0,0,0,0.4) !important;
      }
      /* New container class detected */
      .gh-portal-triggerbtn-container {
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        gap: 8px !important;
        background: rgba(0,0,0,0.6) !important;
        color: ${colorText} !important;
        border: 1px solid ${colorBorder} !important;
        border-radius: 0 !important;
        box-shadow: none !important;
        text-transform: uppercase !important;
        letter-spacing: 0.06em !important;
        height: 40px !important;
        line-height: 40px !important;
        padding: 0 16px !important;
        cursor: pointer !important;
      }
      .gh-portal-triggerbtn-container:hover {
        color: ${colorPrimary} !important;
        border-color: ${colorPrimary} !important;
        background: rgba(0,0,0,0.4) !important;
      }
      .gh-portal-triggerbtn-label {
        color: inherit !important;
        font-size: 1.3rem !important;
        line-height: 1 !important;
        padding: 0 !important;
        margin: 0 !important;
      }
      .gh-portal-triggerbtn * { color: inherit !important; }
      .gh-portal-triggerbtn .gh-portal-btn-text { color: inherit !important; }
      .gh-portal-triggerbtn svg { filter: none !important; }
      .gh-portal-triggerbtn:before, .gh-portal-triggerbtn:after { display: none !important; }
      .gh-portal-triggerbtn.is-loading { opacity: 0.8 !important; }
      .gh-portal-triggerbtn:focus { outline: none !important; box-shadow: none !important; }
      .gh-portal-triggerbtn:hover,
      button.gh-portal-triggerbtn:hover,
      a.gh-portal-triggerbtn:hover {
        color: ${colorPrimary} !important;
        border-color: ${colorPrimary} !important;
        background: rgba(0,0,0,0.4) !important;
      }
    `;

    shadow.appendChild(style);

    // Inject full modal styling for a dark, minimal look
    if (!shadow.getElementById('theme-portal-modal-style')) {
        const modalStyle = document.createElement('style');
        modalStyle.id = 'theme-portal-modal-style';
        modalStyle.textContent = `
          :host { --brandcolor: ${colorPrimary}; }
          /* Backdrop and containers */
          .gh-portal-backdrop { background: rgba(0,0,0,0.7) !important; }
          .gh-portal-popup-container, .gh-portal-popup, .gh-portal-content, .gh-portal { 
            background: ${colorBgSecondary} !important; 
            color: ${colorText} !important; 
            border: 1px solid ${colorBorder} !important; 
            border-radius: 0 !important; 
            box-shadow: none !important; 
            font-family: var(--font-sans-serif) !important;
          }
          [class^="gh-portal-"], [class*=" gh-portal-"] { font-family: var(--font-sans-serif) !important; }
          /* Headings & text */
          .gh-portal-popup-header, .gh-portal-header, .gh-portal-title, .gh-portal-section-title {
            color: ${colorText} !important;
          }
          .gh-portal-subtitle, .gh-portal-text, .gh-portal-disclaimer, .gh-portal-free-ctatext, .gh-portal-input-label {
            color: ${colorTextMuted} !important;
          }
          /* Inputs */
          .gh-portal-input, .gh-portal-input input, input.gh-input, .gh-form-field input, .gh-portal-email, .gh-portal-name, .gh-portal-password {
            background: ${colorBg} !important;
            color: ${colorText} !important;
            border: 1px solid ${colorBorder} !important;
            border-radius: 0 !important;
            box-shadow: none !important;
          }
          .gh-portal-input input::placeholder { color: ${colorTextMuted} !important; }
          /* Buttons inside portal */
          .gh-portal-btn, .gh-portal-btn-main, .gh-portal-btn-primary, .gh-portal-btn-secondary, button.gh-portal-btn, a.gh-portal-btn {
            background: transparent !important;
            color: ${colorText} !important;
            border: 1px solid ${colorBorder} !important;
            border-radius: 0 !important;
            text-transform: uppercase !important;
            letter-spacing: 0.06em !important;
            box-shadow: none !important;
          }
          .gh-portal-btn:hover, .gh-portal-btn-main:hover, .gh-portal-btn-primary:hover, .gh-portal-btn-secondary:hover {
            color: ${colorPrimary} !important;
            border-color: ${colorPrimary} !important;
            background: rgba(0,0,0,0.4) !important;
          }
          .gh-portal-btn-primary { color: ${colorPrimary} !important; border-color: ${colorPrimary} !important; }
          /* Links */
          .gh-portal-link, .gh-portal-disclaimer a, .gh-portal-text a { color: ${colorPrimary} !important; }
          .gh-portal-link:hover, .gh-portal-disclaimer a:hover, .gh-portal-text a:hover { opacity: 0.9 !important; }
          /* Close button / icons */
          .gh-portal-closeicon, .gh-portal-close { filter: none !important; }
          .gh-portal-popup-close { color: ${colorText} !important; }
          .gh-portal-popup-close:hover { color: ${colorPrimary} !important; }
          /* Plans & pricing cards */
          .gh-portal-product-card, .gh-portal-list, .gh-portal-listitem { 
            background: ${colorBg} !important; 
            color: ${colorText} !important; 
            border: 1px solid ${colorBorder} !important; 
            border-radius: 0 !important; 
            box-shadow: none !important; 
          }
          .gh-portal-listitem:hover { border-color: ${colorPrimary} !important; }
          /* Account area */
          .gh-portal-account-main, .gh-portal-settings-main { background: ${colorBgSecondary} !important; }
          .gh-portal-account-section, .gh-portal-settings-section { border-top: 1px solid ${colorBorder} !important; }
          /* Notices */
          .gh-portal-notification, .gh-portal-banner { 
            background: ${colorBg} !important; 
            color: ${colorText} !important; 
            border: 1px solid ${colorBorder} !important; 
            border-radius: 0 !important; 
            box-shadow: none !important; 
          }
        `;
        shadow.appendChild(modalStyle);
    }
    return true;
}

function findPortalFrameDocument() {
    // Ghost Portal trigger can render inside an iframe
    const iframe = document.querySelector('iframe.gh-portal-triggerbtn-iframe,[data-testid="portal-trigger-frame"]');
    if (!iframe) return null;
    try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document || null;
        return doc && doc.readyState !== 'uninitialized' ? doc : null;
    } catch (_) {
        return null;
    }
}

// Attempts to find the main Portal CONTENT iframe (signin/signup/account modal)
function findPortalContentFrameDocument() {
    // Prefer iframes inside the portal shadow root
    const shadow = findPortalShadowRoot();
    if (shadow) {
        const iframes = Array.from(shadow.querySelectorAll('iframe'));
        for (const iframe of iframes) {
            try {
                const doc = iframe.contentDocument || iframe.contentWindow?.document;
                if (!doc) continue;
                if (
                    doc.querySelector('.gh-portal, .gh-portal-popup, .gh-portal-content, .gh-portal-popup-container') ||
                    doc.querySelector('[data-testid="portal-root"], [data-testid="portal-frame-root"]')
                ) {
                    return doc;
                }
            } catch (_) {}
        }
    }
    // Fallback: scan top-level iframes
    const candidates = Array.from(document.querySelectorAll('iframe'));
    for (const iframe of candidates) {
        try {
            const doc = iframe.contentDocument || iframe.contentWindow?.document;
            if (!doc) continue;
            if (
                doc.querySelector('.gh-portal, .gh-portal-popup, .gh-portal-content, .gh-portal-popup-container') ||
                doc.querySelector('[data-testid="portal-root"], [data-testid="portal-frame-root"]')
            ) {
                return doc;
            }
        } catch (_) {}
    }
    return null;
}

function injectStylesIntoPortalContentFrame() {
    const doc = findPortalContentFrameDocument();
    if (!doc) return false;
    if (doc.getElementById('theme-portal-content-style')) return true;

    // Ensure IBM Plex Sans & Bokor are available inside the portal iframe using local CSS
    try {
        if (!doc.getElementById('theme-portal-font')) {
            const link = doc.createElement('link');
            link.id = 'theme-portal-font';
            link.rel = 'stylesheet';
            link.href = (document.querySelector('link[href*="/built/index.css"]')?.href || '').replace('/built/index.css', '/css/fonts.css')
                || (window.__GHOST_ASSET_BASE__ ? window.__GHOST_ASSET_BASE__ + 'assets/css/fonts.css' : '/assets/css/fonts.css');
            (doc.head || doc.body || doc.documentElement).appendChild(link);
        }
    } catch (_) {}

    const docStyles = getComputedStyle(document.documentElement);
    const colorText = docStyles.getPropertyValue('--color-text')?.trim() || '#f5f5f5';
    const colorBorder = docStyles.getPropertyValue('--color-border')?.trim() || '#2a2a2a';
    const colorPrimary = docStyles.getPropertyValue('--color-primary')?.trim() || '#1e40af';
    const colorBg = docStyles.getPropertyValue('--color-bg')?.trim() || '#0a0a0a';
    const colorBgSecondary = docStyles.getPropertyValue('--color-bg-secondary')?.trim() || '#1a1a1a';
    const colorTextMuted = docStyles.getPropertyValue('--color-text-muted')?.trim() || '#9ca3af';

    const style = doc.createElement('style');
    style.id = 'theme-portal-content-style';
    style.textContent = `
      :root { --brandcolor: ${colorPrimary}; }
      html, body, #root { background: ${colorBgSecondary} !important; }
      html, body, * { font-family: var(--font-sans-serif, "IBM Plex Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif) !important; -webkit-font-smoothing: antialiased !important; -moz-osx-font-smoothing: grayscale !important; }
      html, body { font-size: 16px !important; line-height: 1.5 !important; color: ${colorText} !important; }
      /* Backdrop and containers */
      .gh-portal-backdrop { background: rgba(0,0,0,0.7) !important; }
      .gh-portal, .gh-portal-popup, .gh-portal-content, .gh-portal-popup-container {
        background: ${colorBgSecondary} !important;
        color: ${colorText} !important;
        border: 1px solid ${colorBorder} !important;
        border-radius: 0 !important;
        box-shadow: none !important;
        max-width: 640px !important;
        width: calc(100% - 32px) !important;
      }
      /* Layout spacing */
      .gh-portal-popup, .gh-portal-content { padding: 2.4rem !important; }
      .gh-portal-popup-header { margin-bottom: 1.6rem !important; }
      .gh-portal-footer { margin-top: 1.6rem !important; }
      .gh-portal-input, .gh-form-field { margin-bottom: 1.2rem !important; }
      .gh-portal-btn, .gh-portal-btn-main { margin-top: 1.2rem !important; }
      .gh-portal-actions { gap: 0.8rem !important; }
      .gh-portal-btn, .gh-portal-btn-main { min-height: 44px !important; font-size: 0.95rem !important; }
      .gh-portal-content p, .gh-portal-text, .gh-portal-disclaimer, .gh-portal-subtitle { margin: 0.75rem 0 !important; }
      /* Title uses display font Bokor */
      .gh-portal-title, .gh-portal-popup-header .gh-portal-title, .gh-portal-header .gh-portal-title,
      .gh-portal-popup-header h1, .gh-portal-popup-header h2, .gh-portal-header h1, .gh-portal-header h2 {
        font-family: var(--font-display, "Bokor", serif) !important;
        letter-spacing: 0.01em !important;
        margin: 0 0 0.25rem 0 !important;
        line-height: 1.2 !important;
        text-align: center !important;
      }
      /* Align title weight nicely, keep body text sans */
      .gh-portal-title { font-weight: 400 !important; font-size: 1.6rem !important; }
      /* Secondary small text */
      .gh-portal-popup-header small, .gh-portal-subtitle { line-height: 1.4 !important; }
      .gh-portal-input-label { font-size: 0.8rem !important; letter-spacing: 0.02em !important; }
      [class^="gh-portal-"], [class*=" gh-portal-"] { font-family: var(--font-sans-serif) !important; }
      .gh-portal-title, .gh-portal-section-title { font-weight: 600 !important; letter-spacing: 0.01em !important; }
      /* Headings & text */
      .gh-portal-popup-header, .gh-portal-header, .gh-portal-title, .gh-portal-section-title { color: ${colorText} !important; }
      .gh-portal-subtitle, .gh-portal-text, .gh-portal-disclaimer, .gh-portal-free-ctatext, .gh-portal-input-label { color: ${colorTextMuted} !important; }
      /* Inputs */
      .gh-portal-input, .gh-portal-input input, input.gh-input, .gh-form-field input, .gh-portal-email, .gh-portal-name, .gh-portal-password {
        background: ${colorBg} !important;
        color: ${colorText} !important;
        border: 1px solid ${colorBorder} !important;
        border-radius: 0 !important;
        box-shadow: none !important;
        height: 44px !important;
      }
      .gh-portal-input input { padding: 10px 12px !important; font-size: 0.95rem !important; }
      .gh-portal-input { gap: 0.4rem !important; }
      .gh-portal .gh-input-group { margin-top: 0.4rem !important; }
      .gh-portal .gh-input-error { color: ${colorPrimary} !important; }
      .gh-portal .gh-input-message { color: ${colorTextMuted} !important; }
      .gh-portal .gh-input + .gh-input { margin-top: 0.8rem !important; }
      .gh-portal .gh-form { gap: 0.8rem !important; }
      .gh-portal .gh-portal-signup .gh-portal-form { gap: 0.8rem !important; }
      .gh-portal-input input::placeholder { color: ${colorTextMuted} !important; }
      .gh-portal-input input:focus { outline: none !important; border-color: ${colorPrimary} !important; box-shadow: 0 0 0 3px rgba(30, 64, 175, 0.08) !important; }
      /* Buttons */
      .gh-portal-btn, .gh-portal-btn-main, .gh-portal-btn-primary, .gh-portal-btn-secondary, button.gh-portal-btn, a.gh-portal-btn {
        background: transparent !important;
        color: ${colorText} !important;
        border: 1px solid ${colorBorder} !important;
        border-radius: 0 !important;
        text-transform: uppercase !important;
        letter-spacing: 0.06em !important;
        box-shadow: none !important;
      }
      .gh-portal-btn:hover, .gh-portal-btn-main:hover, .gh-portal-btn-primary:hover, .gh-portal-btn-secondary:hover { color: ${colorPrimary} !important; border-color: ${colorPrimary} !important; background: rgba(0,0,0,0.4) !important; }
      .gh-portal-btn-primary { color: ${colorPrimary} !important; border-color: ${colorPrimary} !important; }
      .gh-portal a { color: ${colorPrimary} !important; }
      .gh-portal a:hover { opacity: 0.9 !important; }
      @media (max-width: 480px) {
        .gh-portal-popup, .gh-portal-content { padding: 1.6rem !important; }
        .gh-portal-title { font-size: 1.4rem !important; }
      }
      /* Links */
      .gh-portal-link, .gh-portal-disclaimer a, .gh-portal-text a { color: ${colorPrimary} !important; }
      .gh-portal-link:hover, .gh-portal-disclaimer a:hover, .gh-portal-text a:hover { opacity: 0.9 !important; }
      /* Close */
      .gh-portal-closeicon, .gh-portal-close, .gh-portal-popup-close { color: ${colorText} !important; }
      .gh-portal-popup-close:hover { color: ${colorPrimary} !important; }
    `;
    (doc.head || doc.body || doc).appendChild(style);
    return true;
}
function injectStylesIntoPortalFrame() {
    const doc = findPortalFrameDocument();
    if (!doc) return false;
    const rootEl = doc.documentElement || doc.body || doc;
    if (!rootEl.querySelector('.gh-portal-triggerbtn-container') && !rootEl.querySelector('.gh-portal-triggerbtn') && !rootEl.querySelector('.gh-portal')) return false;

    if (doc.getElementById('theme-portal-trigger-style')) return true;

    const docStyles = getComputedStyle(document.documentElement);
    const colorText = docStyles.getPropertyValue('--color-text')?.trim() || '#f5f5f5';
    const colorBorder = docStyles.getPropertyValue('--color-border')?.trim() || '#2a2a2a';
    const colorPrimary = docStyles.getPropertyValue('--color-primary')?.trim() || '#1e40af';
    const colorBg = docStyles.getPropertyValue('--color-bg')?.trim() || '#0a0a0a';
    const colorBgSecondary = docStyles.getPropertyValue('--color-bg-secondary')?.trim() || '#1a1a1a';
    const colorTextMuted = docStyles.getPropertyValue('--color-text-muted')?.trim() || '#9ca3af';

    const style = doc.createElement('style');
    style.id = 'theme-portal-trigger-style';
    style.textContent = `
      /* Force-hide portal trigger button inside iframe */
      .gh-portal-triggerbtn-wrap,
      .gh-portal-triggerbtn-wrapper,
      .gh-portal-triggerbtn-container,
      .gh-portal-triggerbtn,
      [data-testid="portal-trigger-button"] {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }
      :root { --brandcolor: ${colorPrimary}; }
      html, body, * { font-family: var(--font-sans-serif) !important; }
      .gh-portal-triggerbtn-wrap, .gh-portal-triggerbtn-wrap *,
      .gh-portal-triggerbtn-wrapper, .gh-portal-triggerbtn-wrapper *,
      .gh-portal-triggerbtn-container, .gh-portal-triggerbtn-container * {
        font-family: var(--font-sans-serif) !important;
      }
      .gh-portal-triggerbtn-wrap { right: 24px !important; bottom: 24px !important; }
      .gh-portal-triggerbtn,
      button.gh-portal-triggerbtn,
      a.gh-portal-triggerbtn,
      .gh-portal-triggerbtn-container {
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        gap: 8px !important;
        background: rgba(0,0,0,0.6) !important;
        color: ${colorText} !important;
        border: 1px solid ${colorBorder} !important;
        border-radius: 0 !important;
        box-shadow: none !important;
        text-transform: uppercase !important;
        letter-spacing: 0.06em !important;
        height: 40px !important;
        line-height: 40px !important;
        padding: 0 16px !important;
        background-image: none !important;
        cursor: pointer !important;
      }
      .gh-portal-triggerbtn-container:hover,
      .gh-portal-triggerbtn:hover,
      button.gh-portal-triggerbtn:hover,
      a.gh-portal-triggerbtn:hover {
        color: ${colorPrimary} !important;
        border-color: ${colorPrimary} !important;
        background: rgba(0,0,0,0.4) !important;
      }
      .gh-portal-triggerbtn-label, .gh-portal-btn-text { color: inherit !important; font-size: 1.3rem !important; line-height: 1 !important; padding: 0 !important; margin: 0 !important; }
      a.gh-button.gh-portal-close { background: rgba(0,0,0,0.6) !important; color: ${colorText} !important; border: 1px solid ${colorBorder} !important; border-radius: 0 !important; box-shadow: none !important; text-transform: uppercase !important; letter-spacing: 0.06em !important; height: 40px !important; line-height: 40px !important; padding: 0 16px !important; }
      a.gh-button.gh-portal-close:hover { color: ${colorPrimary} !important; border-color: ${colorPrimary} !important; background: rgba(0,0,0,0.4) !important; }
      /* Full modal styles if content is inside this frame */
      .gh-portal-backdrop { background: rgba(0,0,0,0.7) !important; }
      .gh-portal-popup-container, .gh-portal-popup, .gh-portal-content, .gh-portal { 
        background: ${colorBgSecondary} !important; 
        color: ${colorText} !important; 
        border: 1px solid ${colorBorder} !important; 
        border-radius: 0 !important; 
        box-shadow: none !important; 
        font-family: var(--font-sans-serif) !important;
      }
      [class^="gh-portal-"], [class*=" gh-portal-"] { font-family: var(--font-sans-serif) !important; }
      .gh-portal-subtitle, .gh-portal-text, .gh-portal-disclaimer, .gh-portal-free-ctatext, .gh-portal-input-label { color: ${colorTextMuted} !important; }
      .gh-portal-input, .gh-portal-input input, input.gh-input, .gh-form-field input, .gh-portal-email, .gh-portal-name, .gh-portal-password {
        background: ${colorBg} !important; color: ${colorText} !important; border: 1px solid ${colorBorder} !important; border-radius: 0 !important; box-shadow: none !important;
      }
      .gh-portal-input input::placeholder { color: ${colorTextMuted} !important; }
      .gh-portal-btn, .gh-portal-btn-main, .gh-portal-btn-primary, .gh-portal-btn-secondary, button.gh-portal-btn, a.gh-portal-btn {
        background: transparent !important; color: ${colorText} !important; border: 1px solid ${colorBorder} !important; border-radius: 0 !important; text-transform: uppercase !important; letter-spacing: 0.06em !important; box-shadow: none !important;
      }
      .gh-portal-btn:hover, .gh-portal-btn-main:hover, .gh-portal-btn-primary:hover, .gh-portal-btn-secondary:hover { color: ${colorPrimary} !important; border-color: ${colorPrimary} !important; background: rgba(0,0,0,0.4) !important; }
      .gh-portal-btn-primary { color: ${colorPrimary} !important; border-color: ${colorPrimary} !important; }
    `;
    (doc.head || doc.body || doc).appendChild(style);
    return true;
}

function setupPortalStyling() {
    let appliedShadow = injectStylesIntoPortal();
    let appliedTriggerFrame = injectStylesIntoPortalFrame();
    let appliedContentFrame = injectStylesIntoPortalContentFrame();
    const observer = new MutationObserver(() => {
        if (!appliedShadow) appliedShadow = injectStylesIntoPortal();
        if (!appliedTriggerFrame) appliedTriggerFrame = injectStylesIntoPortalFrame();
        if (!appliedContentFrame) appliedContentFrame = injectStylesIntoPortalContentFrame();
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
    // Also try a few times with timeouts for safety
    let attempts = 0;
    const interval = setInterval(() => {
        const ok = injectStylesIntoPortal() || injectStylesIntoPortalFrame() || injectStylesIntoPortalContentFrame();
        if (ok || attempts++ > 40) clearInterval(interval);
    }, 250);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupPortalStyling);
} else {
    setupPortalStyling();
}


